import Text from "app/styles/Text";
import { ReactComponent as SuccessCheck } from "app/assets/img/success-check.svg";
import { useAppState } from "app/providers/AppStateProvider";
import { formatMoneyNoCurrency } from "app/lib/money";
import { useEffect, useMemo } from "react";
import { Badge } from "app/components/Badge";
import { capitalizeStr } from "app/lib/string";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

dayjs.extend(advancedFormat);

export default function Completed() {
  const { payment, merchant, payment: paymentData } = useAppState();

  useEffect(() => {
    setTimeout(() => {
      if (merchant?.callback_url) {
        window.location.href = merchant?.callback_url;
        return;
      }

    }, 4000);
  }, [merchant?.callback_url]);

  const status =
    paymentData?.timeline?.events[paymentData?.timeline?.events?.length - 1]?.done;

  const feesObject = useMemo(
    () =>
      paymentData?.fees.reduce((acc, item) => {
        const key = capitalizeStr(item.name.replace(/_/g, " "));
        acc[key] = (
          <Text semiBold>
            {`${formatMoneyNoCurrency(item.amount.amount, "digital")} ${item.amount.currency
              }`}
          </Text>
        );
        return acc;
      }, {} as Record<string, React.ReactNode>),
    [paymentData?.fees]
  );

  const totalFess = useMemo(() => {
    return paymentData?.fees?.reduce(
      (sum, fee) => sum + parseFloat(fee.amount.amount),
      0
    );
  }, [paymentData?.fees]);

  const details = {
    From: (
      <div tw="flex items-center gap-[8px]">
        <Text semiBold tw="max-w-[200px] truncate">
          {payment?.pay_in?.type === "address" ? "Address" : "Balance"}
        </Text>
      </div>
    ),
    "Order code": (
      <div tw="flex items-center gap-[8px]">
        <Text semiBold tw="max-w-[200px] truncate">
          {payment?.reference}
        </Text>
      </div>
    ),
    "Exchange rate": (
      <Text semiBold>{paymentData?.quote_rate?.rate_explained}</Text>
    ),
    Subtotal: (
      <Text semiBold>
        {formatMoneyNoCurrency(
          paymentData?.source_amount ? +paymentData?.source_amount : 0,
          "digital"
        )}{" "}
        {paymentData?.source_currency}
      </Text>
    ),
    ...feesObject,
    "Date and time": (
      <Text semiBold>
        {dayjs(paymentData?.updated_at).format("MMMM Do, YYYY, HH:MMA")}
      </Text>
    ),

    Total: (
      <Text semiBold>
        {formatMoneyNoCurrency(
          totalFess +
          (paymentData?.source_amount ? +paymentData?.source_amount : 0),
          "digital"
        )}{" "}
        {paymentData?.source_currency}
      </Text>
    ),
    Status: (
      <Badge variant={!status ? "warning" : "success"}>
        {status ? "Completed" : "Processing"}
      </Badge>
    ),
  };

  return (
    <div tw="flex flex-col gap-[24px] items-center text-center h-full">
      <div tw="flex flex-col gap-[16px]">
        <SuccessCheck tw="w-[140px] h-[64px] mx-auto" />
        <div tw="flex flex-col gap-[8px]">
          <Text xBig semiBold>
            Payment successful
          </Text>

          <Text subdued medium>
            You made a payment of{" "}
            <span tw="font-semibold text-high">
              {formatMoneyNoCurrency(+payment?.source_amount!, "crypto")}{" "}
              {payment?.source_currency}
            </span>{" "}
            to {merchant?.business_name}. {merchant?.callback_url ? "Redirecting you to your home page in a few seconds" : null}
          </Text>
        </div>
      </div>

      {paymentData && payment && (
        <>
          <div tw="bg-containment-tertiary rounded-[16px] p-[16px] flex flex-col gap-[24px] w-full">
            <div tw="flex flex-col gap-[24px]">
              {Object.keys(details).map((item) => (
                <div tw="flex  gap-[8px] items-center justify-between">
                  <Text tw="whitespace-nowrap" subdued>
                    {item}
                  </Text>
                  {details[item as keyof typeof details]}
                </div>
              ))}
            </div>
          </div>

          {/* <Button fullWidth size="xBig" variant="secondary">
            Download receipt
          </Button> */}

          <div>
            <Text small subdued>
              Need help? Contact{" "}
              <a
                href="mailto:support@busha.co"
                tw="text-action-constructive font-semibold"
              >
                support@busha.co
              </a>
            </Text>
          </div>
        </>
      )}
    </div>
  );
}
